<template>
  <div class="lux-page">
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>
    <div class="glow glow-1"></div>
    <div class="glow glow-2"></div>
    <div class="glow glow-3"></div>

    <div class="frame">
      <header class="topbar">
        <button type="button" class="icon-btn" @click="back">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 6.5 9 12l5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <button type="button" class="lang-btn" @click="langOpen = true">
          <img :src="currentLangMeta.icon" alt="" />
          <span>{{ currentLangMeta.code }}</span>
          <svg class="chev" :class="{ open: langOpen }" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m7 10 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </header>

      <main class="stage">
        <div class="hero">
          <div class="logo-aura">
            <div class="logo-halo"></div>
            <div class="logo-shell">
              <div class="logo-core">
                <img class="logo-img" :src="logoSrc" alt="Soulmate" @error="onLogoError" />
              </div>
            </div>
          </div>
          <h1>SOULMATE</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <div class="glass-card">
          <div class="card-line"></div>
          <form @submit.prevent="doLogin">
            <div class="field">
              <label>{{ copy.userLabel }}</label>
              <div class="glass-input">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M5.5 18.5c1.2-2.6 3.4-3.8 6.5-3.8s5.3 1.2 6.5 3.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
                <input
                  v-model.trim="username"
                  type="text"
                  autocomplete="username"
                  :placeholder="copy.userPlaceholder"
                />
              </div>
            </div>

            <div class="field">
              <div class="field-head">
                <label>{{ copy.passLabel }}</label>
               <!-- <button type="button" class="forgot" @click="toForgetPwd">{{ copy.forgot }}</button> -->
              </div>
              <div class="glass-input">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="6" y="10.5" width="12" height="8.5" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M8.5 10.5V8.2a3.5 3.5 0 0 1 7 0v2.3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
                <input
                  v-model="password"
                  :type="passwordType"
                  autocomplete="current-password"
                  placeholder="••••••••••••"
                />
                <button type="button" class="eye" @click="switchPasswordType">
                  <svg v-if="passwordType === 'password'" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" fill="none" stroke="currentColor" stroke-width="1.6"/>
                    <path d="m4 5 16 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" fill="none" stroke="currentColor" stroke-width="1.6"/>
                    <circle cx="12" cy="12" r="2.4" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  </svg>
                </button>
              </div>
            </div>

            <button class="login-btn" type="submit" :disabled="loading">
              <span class="btn-label">{{ loading ? copy.loading : copy.submit }}</span>
              <span v-if="loading" class="btn-spin"></span>
              <svg v-else class="btn-arrow" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </form>

          <div class="card-foot">
            <button type="button" class="register-link" @click="toRegister">
              <span>{{ copy.noAccount }}</span>
              <strong>{{ copy.create }}</strong>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </main>

      <footer class="legal">
        <span>© 2026 Soulmate Private Club</span>
        <span>All Rights Reserved</span>
      </footer>
    </div>

    <transition name="sheet">
      <div v-if="langOpen" class="lang-mask" @click.self="langOpen = false">
        <div class="lang-sheet">
          <div class="sheet-head">
            <h3>{{ copy.langTitle }}</h3>
            <button type="button" class="sheet-close" @click="langOpen = false">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m7 7 10 10M17 7 7 17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <button
            v-for="item in langMeta"
            :key="item.key"
            type="button"
            class="lang-option"
            :class="{ active: lang === item.key }"
            @click="selectLanguage(item.key)"
          >
            <span class="lang-left">
              <img :src="item.icon" alt="" />
              <span>
                <strong>{{ item.name }}</strong>
                <small>{{ item.region }}</small>
              </span>
            </span>
            <svg v-if="lang === item.key" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m5 12.5 4.2 4.2L19 7.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const LANG_META = [
  { key: 'en_us', code: 'EN', name: 'English', region: 'United States', icon: require('../images/language/en_us.png') },
  { key: 'zh_cn', code: 'ZH', name: '繁体中文', region: '中文', icon: require('../images/language/zh_cn.png') },
  { key: 'es_spa', code: 'ES', name: 'Español', region: 'España', icon: require('../images/language/es_spa.png') },
  { key: 'ms_my', code: 'DE', name: 'Deutsch', region: 'Deutschland', icon: require('../images/language/ms_my.png') },
  { key: 'yn_yu', code: 'ID', name: 'Indonesia', region: 'Bahasa Indonesia', icon: require('../images/language/yn_yu.png') }
];

const COPY = {
  en_us: {
    subtitle: 'Welcome Back to Luxury',
    userLabel: 'Username or Email',
    userPlaceholder: 'Enter your credentials',
    passLabel: 'Password',
    forgot: 'Forgot?',
    submit: 'Sign In',
    loading: 'Authenticating',
    noAccount: "Don't have an account?",
    create: 'Create Access',
    langTitle: 'Select Preferred Language'
  },
  zh_cn: {
    subtitle: '尊享臻致 · 歡迎歸來',
    userLabel: '帳號或電子郵箱',
    userPlaceholder: '請輸入帳號',
    passLabel: '登入密碼',
    forgot: '忘記密碼？',
    submit: '立即登入',
    loading: '正在驗證',
    noAccount: '尚未擁有專屬帳號？',
    create: '申請加入',
    langTitle: '選擇語言'
  },
  es_spa: {
    subtitle: 'Bienvenido a la Elegancia',
    userLabel: 'Usuario o Correo',
    userPlaceholder: 'Ingresa tus datos',
    passLabel: 'Contraseña',
    forgot: '¿Olvidó?',
    submit: 'Iniciar sesión',
    loading: 'Verificando',
    noAccount: '¿No tiene una cuenta?',
    create: 'Crear acceso',
    langTitle: 'Seleccionar idioma'
  },
  ms_my: {
    subtitle: 'Willkommen zurück',
    userLabel: 'Benutzername oder E-Mail',
    userPlaceholder: 'Zugangsdaten eingeben',
    passLabel: 'Passwort',
    forgot: 'Vergessen?',
    submit: 'Anmelden',
    loading: 'Prüfung läuft',
    noAccount: 'Noch kein Konto?',
    create: 'Zugang erstellen',
    langTitle: 'Sprache wählen'
  },
  yn_yu: {
    subtitle: 'Selamat datang kembali',
    userLabel: 'Nama pengguna',
    userPlaceholder: 'Masukkan akun Anda',
    passLabel: 'Kata sandi',
    forgot: 'Lupa?',
    submit: 'Masuk',
    loading: 'Memverifikasi',
    noAccount: 'Belum punya akun?',
    create: 'Daftar sekarang',
    langTitle: 'Pilih bahasa'
  }
};

const LANG_MAP = {
  en: 'en_us',
  'en-us': 'en_us',
  en_us: 'en_us',
  zh: 'zh_cn',
  'zh-cn': 'zh_cn',
  zh_cn: 'zh_cn',
  es: 'es_spa',
  'es-es': 'es_spa',
  es_spa: 'es_spa',
  tr: 'es_spa',
  'tr-tr': 'es_spa',
  ms: 'ms_my',
  'ms-my': 'ms_my',
  ms_my: 'ms_my',
  yn_yu: 'yn_yu'
};

export default {
  model: {
    prop: 'inputValue',
    event: 'input'
  },

  props: {
    inputValue: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      username: '',
      lang: 'en_us',
      password: this.inputValue,
      passwordType: 'password',
      loading: false,
      langOpen: false,
      logoFailed: false
    };
  },

  computed: {
    langMeta() {
      return LANG_META;
    },
    currentLangMeta() {
      return LANG_META.find((item) => item.key === this.lang) || LANG_META[0];
    },
    copy() {
      return COPY[this.lang] || COPY.en_us;
    },
    logoSrc() {
      const info = this.$store.getters.getBaseInfo || {};
      const source = typeof info.ico === 'string' ? info.ico.trim() : '';
      return !this.logoFailed && source ? source : '/img/null.png';
    }
  },

  created() {
    this.loadLuxuryFonts();

    if (localStorage.getItem('token')) {
      return window.history.back();
    }

    const rawLang = (
      localStorage.getItem('lang') ||
      this.$i18n.locale ||
      'en_us'
    ).toLowerCase();

    this.lang = LANG_MAP[rawLang] || 'en_us';
    this.$i18n.locale = this.lang;
    localStorage.setItem('lang', this.lang);
  },

  mounted() {
    document.documentElement.classList.add('luxury-login-root');
    document.body.classList.add('luxury-login-root');
  },

  beforeDestroy() {
    this._particleStopped = true;
    if (this._particleFrame) {
      cancelAnimationFrame(this._particleFrame);
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    document.documentElement.classList.remove('luxury-login-root');
    document.body.classList.remove('luxury-login-root');
  },

  methods: {
    loadLuxuryFonts() {
      if (document.getElementById('luxury-login-fonts')) {
        return;
      }
      const link = document.createElement('link');
      link.id = 'luxury-login-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(link);
    },

    initParticles() {
      const canvas = this.$refs.particleCanvas;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext('2d');
      const resize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };
      resize();
      this._onResize = resize;
      window.addEventListener('resize', resize);

      const particles = Array.from({ length: 35 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.8 + 0.5,
        alpha: Math.random() * 0.6 + 0.1,
        vx: (Math.random() - 0.5) * 0.3,
        vy: -Math.random() * 0.4 - 0.1
      }));

      const tick = () => {
        if (this._particleStopped) {
          return;
        }
        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);
        particles.forEach((particle) => {
          particle.x += particle.vx;
          particle.y += particle.vy;
          if (particle.y < 0) {
            particle.y = height;
          }
          if (particle.x < 0 || particle.x > width) {
            particle.x = Math.random() * width;
          }
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(244, 216, 131, ' + particle.alpha + ')';
          ctx.shadowBlur = 8;
          ctx.shadowColor = 'rgba(212, 175, 55, 0.8)';
          ctx.fill();
        });
        this._particleFrame = requestAnimationFrame(tick);
      };

      tick();
    },

    switchPasswordType() {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    },

    back() {
      return window.history.back();
    },

    toRegister() {
      this.$router.push('Register');
    },

    toForgetPwd() {
      // this.$router.push('Forgetpwd');
    },

    onLogoError() {
      this.logoFailed = true;
    },

    selectLanguage(lang) {
      this.lang = lang;
      this.$i18n.locale = lang;
      localStorage.setItem('lang', lang);
      this.langOpen = false;
    },

    doLogin() {
      if (!this.username) {
        this.$toast(this.$t('auth.username_place'));
        return false;
      }

      if (!this.password) {
        this.$toast(this.$t('auth.pwd_place'));
        return false;
      }

      this.loading = true;

      this.$http({
        url: 'member_login',
        method: 'post',
        data: {
          username: this.username,
          password: this.password,
          lang: this.lang
        }
      })
        .then((res) => {
          this.loading = false;
          if (res.code === 200) {
            this.$toast.success(res.msg);
            localStorage.setItem('token', res.data.id);
            this.$router.push('/Home');
          } else {
            this.$toast(res.msg);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style>
.luxury-login-root {
  background: #07050B;
}
</style>

<style lang="less" scoped>
.lux-page {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  background: #07050B;
  color: #E2E8F0;
  font-family: "Plus Jakarta Sans", "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-tap-highlight-color: transparent;
}

.particle-canvas {
  position: fixed;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.4;
}

.glow {
  position: fixed;
  z-index: 0;
  border-radius: 50%;
  pointer-events: none;
}

.glow-1 {
  top: -10%;
  right: -15%;
  width: 380PX;
  height: 380PX;
  background: radial-gradient(circle at 30% 30%, rgba(88, 28, 135, 0.32), rgba(212, 175, 55, 0.16) 42%, transparent 70%);
  filter: blur(90PX);
  animation: floatGlow 12s ease-in-out infinite;
}

.glow-2 {
  bottom: -10%;
  left: -15%;
  width: 420PX;
  height: 420PX;
  background: radial-gradient(circle at 70% 40%, rgba(170, 134, 37, 0.28), rgba(30, 27, 75, 0.4) 46%, transparent 72%);
  filter: blur(100PX);
  animation: floatGlow 16s ease-in-out infinite reverse;
}

.glow-3 {
  top: 35%;
  left: 50%;
  width: 300PX;
  height: 300PX;
  background: rgba(217, 119, 6, 0.12);
  filter: blur(110PX);
  transform: translateX(-50%);
}

.frame {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-width: 430PX;
  min-height: 100vh;
  margin: 0 auto;
  background: rgba(12, 10, 20, 0.8);
  backdrop-filter: blur(40PX);
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(28PX + env(safe-area-inset-top)) 24PX 16PX;
}

.icon-btn,
.lang-btn,
.eye,
.sheet-close,
.forgot,
.register-link,
.lang-option,
.login-btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
}

.icon-btn,
.lang-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44PX;
  border-radius: 16PX;
  background: rgba(255, 255, 255, 0.04);
  box-shadow: 0 10PX 24PX rgba(0, 0, 0, 0.28);
}

.icon-btn {
  width: 44PX;
  color: #F9EAB8;
  border: 1PX solid rgba(255, 255, 255, 0.1);
}

.icon-btn svg,
.eye svg,
.sheet-close svg,
.lang-option > svg,
.btn-arrow,
.register-link svg {
  display: block;
}

.icon-btn svg {
  width: 18PX;
  height: 18PX;
}

.lang-btn {
  gap: 8PX;
  padding: 0 14PX;
  border: 1PX solid rgba(212, 175, 55, 0.2);
  color: #F9EAB8;
}

.lang-btn img,
.lang-left img {
  width: 22PX;
  height: 22PX;
  border-radius: 50%;
  object-fit: cover;
}

.lang-btn span {
  font-size: 12PX;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.chev {
  width: 12PX;
  height: 12PX;
  color: #ECC250;
  transition: transform 0.3s ease;
}

.chev.open {
  transform: rotate(180deg);
}

.stage {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: 8PX 24PX 16PX;
}

.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28PX;
  text-align: center;
}

.logo-aura {
  position: relative;
  width: 96PX;
  height: 96PX;
  margin-bottom: 18PX;
}

.logo-halo {
  position: absolute;
  inset: 0;
  border-radius: 28PX;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.42), rgba(252, 211, 77, 0.28), rgba(147, 51, 234, 0.3));
  filter: blur(18PX);
  opacity: 0.8;
}

.logo-shell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96PX;
  height: 96PX;
  border: 1PX solid rgba(236, 194, 80, 0.3);
  border-radius: 24PX;
  background: linear-gradient(180deg, #1A162B, #0A0812);
  box-shadow: 0 15PX 35PX rgba(0, 0, 0, 0.6);
  animation: pulseLogo 4s ease-in-out infinite;
}

.logo-core {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80PX;
  height: 80PX;
  border: 1PX solid rgba(244, 216, 131, 0.2);
  border-radius: 18PX;
  background: rgba(18, 15, 29, 0.8);
}

.logo-img {
  width: 52PX;
  height: 52PX;
  object-fit: contain;
}

.hero h1 {
  margin: 0;
  font-family: "Cormorant Garamond", "Songti SC", "Noto Serif SC", serif;
  font-size: 40PX;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.08em;
  background: linear-gradient(135deg, #FFF0CA 0%, #F3D079 40%, #C89932 80%, #E6C46E 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero p {
  margin: 8PX 0 0;
  color: rgba(249, 234, 184, 0.62);
  font-size: 12PX;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.glass-card {
  position: relative;
  overflow: hidden;
  padding: 28PX 24PX 22PX;
  border: 1PX solid rgba(244, 216, 131, 0.15);
  border-radius: 32PX;
  background: rgba(18, 14, 29, 0.55);
  box-shadow:
    0 30PX 60PX -12PX rgba(0, 0, 0, 0.75),
    inset 0 1PX 1PX rgba(255, 240, 202, 0.2);
  backdrop-filter: blur(24PX);
}

.card-line {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 1PX;
  background: linear-gradient(90deg, transparent, rgba(244, 216, 131, 0.4), transparent);
}

.field + .field {
  margin-top: 18PX;
}

.field label,
.field-head label {
  display: block;
  margin: 0 0 6PX 2PX;
  color: rgba(249, 234, 184, 0.7);
  font-size: 11PX;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.field-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.forgot {
  margin-bottom: 6PX;
  color: #ECC250;
  font-size: 11PX;
  font-weight: 500;
}

.glass-input {
  display: flex;
  align-items: center;
  gap: 12PX;
  height: 56PX;
  padding: 0 16PX;
  border: 1PX solid rgba(255, 255, 255, 0.08);
  border-radius: 16PX;
  background: rgba(255, 255, 255, 0.03);
  transition: border-color 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1), background 0.35s ease;
}

.glass-input:focus-within {
  border-color: rgba(244, 216, 131, 0.6);
  background: rgba(212, 175, 55, 0.05);
  box-shadow: 0 0 20PX rgba(212, 175, 55, 0.18), inset 0 0 10PX rgba(212, 175, 55, 0.05);
}

.glass-input svg {
  flex: 0 0 auto;
  width: 18PX;
  height: 18PX;
  color: rgba(236, 194, 80, 0.72);
}

.glass-input input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font-size: 14PX;
  font-weight: 400;
}

.glass-input input::placeholder {
  color: rgba(255, 255, 255, 0.22);
}

.eye {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28PX;
  height: 28PX;
  padding: 0;
  color: rgba(255, 255, 255, 0.42);
}

.eye svg {
  width: 16PX;
  height: 16PX;
  color: inherit;
}

.login-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8PX;
  width: 100%;
  height: 56PX;
  margin-top: 22PX;
  overflow: hidden;
  border-radius: 16PX;
  background: linear-gradient(135deg, #F5D77F 0%, #D4AF37 50%, #9B761E 100%);
  box-shadow: 0 10PX 28PX -6PX rgba(212, 175, 55, 0.4);
  color: #120F1D;
  font-size: 14PX;
  font-weight: 650;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.login-btn::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(60deg, transparent 30%, rgba(255, 255, 255, 0.35) 50%, transparent 70%);
  transform: rotate(30deg) translateY(-100%);
  animation: shimmer 3.8s ease-in-out infinite;
}

.login-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.login-btn:disabled {
  opacity: 0.86;
}

.btn-label,
.btn-arrow,
.btn-spin {
  position: relative;
  z-index: 1;
}

.btn-arrow {
  width: 16PX;
  height: 16PX;
}

.btn-spin {
  width: 14PX;
  height: 14PX;
  border: 2PX solid rgba(18, 15, 29, 0.25);
  border-top-color: #120F1D;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.card-foot {
  margin-top: 22PX;
  padding-top: 18PX;
  border-top: 1PX solid rgba(255, 255, 255, 0.05);
  text-align: center;
}

.register-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8PX;
  max-width: 100%;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12PX;
}

.register-link strong {
  color: #ECC250;
  font-weight: 650;
}

.register-link svg {
  width: 14PX;
  height: 14PX;
  color: #ECC250;
}

.legal {
  display: flex;
  flex-direction: column;
  gap: 4PX;
  padding: 18PX 20PX calc(22PX + env(safe-area-inset-bottom));
  color: rgba(255, 255, 255, 0.3);
  font-size: 10PX;
  line-height: 1.4;
  letter-spacing: 0.18em;
  text-align: center;
  text-transform: uppercase;
}

.lang-mask {
  position: fixed;
  z-index: 30;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(12PX);
}

.lang-sheet {
  width: 100%;
  max-width: 430PX;
  max-height: 78vh;
  padding: 22PX 20PX calc(20PX + env(safe-area-inset-bottom));
  overflow-y: auto;
  border-top: 1PX solid rgba(212, 175, 55, 0.3);
  border-radius: 36PX 36PX 0 0;
  background: #120F1D;
  box-shadow: 0 -20PX 50PX rgba(0, 0, 0, 0.8);
}

.sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14PX;
  padding-bottom: 12PX;
  border-bottom: 1PX solid rgba(255, 255, 255, 0.1);
}

.sheet-head h3 {
  margin: 0;
  font-family: "Cormorant Garamond", "Songti SC", serif;
  font-size: 22PX;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: linear-gradient(135deg, #FFF0CA, #C89932);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.sheet-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32PX;
  height: 32PX;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.66);
}

.sheet-close svg {
  width: 14PX;
  height: 14PX;
}

.lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 8PX;
  padding: 12PX 14PX;
  border: 1PX solid rgba(255, 255, 255, 0.05);
  border-radius: 16PX;
  background: rgba(255, 255, 255, 0.03);
  text-align: left;
}

.lang-option.active {
  border-color: rgba(212, 175, 55, 0.35);
  background: rgba(212, 175, 55, 0.1);
}

.lang-left {
  display: flex;
  align-items: center;
  gap: 12PX;
}

.lang-left strong,
.lang-left small {
  display: block;
}

.lang-left strong {
  color: #fff;
  font-size: 14PX;
  font-weight: 550;
}

.lang-left small {
  margin-top: 2PX;
  color: rgba(255, 255, 255, 0.4);
  font-size: 10PX;
}

.lang-option > svg {
  width: 16PX;
  height: 16PX;
  color: #ECC250;
}

.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.3s ease;
}

.sheet-enter-active .lang-sheet,
.sheet-leave-active .lang-sheet {
  transition: transform 0.3s ease;
}

.sheet-enter,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter .lang-sheet,
.sheet-leave-to .lang-sheet {
  transform: translateY(100%);
}

@media (min-width: 768px) {
  .lux-page {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24PX 0;
  }

  .frame {
    min-height: ~"min(880PX, calc(100vh - 48PX))";
    overflow: hidden;
    border: 1PX solid rgba(212, 175, 55, 0.2);
    border-radius: 0;
    box-shadow: 0 0 80PX rgba(0, 0, 0, 0.9);
  }

  .lang-mask {
    align-items: center;
    padding: 24PX;
  }

  .lang-sheet {
    border: 1PX solid rgba(212, 175, 55, 0.3);
    border-radius: 36PX;
  }

  .sheet-enter .lang-sheet,
  .sheet-leave-to .lang-sheet {
    transform: translateY(16PX);
  }
}

@media (min-width: 768px) {
  .lux-page .particle-canvas {
    display: none;
  }

  .lux-page .glow,
  .lux-page .logo-halo {
    filter: none !important;
    animation: none !important;
    opacity: 0.22;
  }

  .lux-page .frame,
  .lux-page .glass-card,
  .lux-page .lang-mask {
    -webkit-backdrop-filter: none !important;
    backdrop-filter: none !important;
  }

  .lux-page .logo-shell,
  .lux-page .login-btn::before {
    animation: none !important;
  }
}

@keyframes floatGlow {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 0.5;
  }
  50% {
    transform: translate3d(20PX, -30PX, 0) scale(1.12);
    opacity: 0.75;
  }
}

@keyframes pulseLogo {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 15PX rgba(212, 175, 55, 0.4));
  }
  50% {
    transform: scale(1.04);
    filter: drop-shadow(0 0 30PX rgba(212, 175, 55, 0.8));
  }
}

@keyframes shimmer {
  0%,
  58% {
    transform: rotate(30deg) translateY(-100%);
  }
  100% {
    transform: rotate(30deg) translateY(100%);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
